import axios from "axios";
import { getAuthHeaders } from "./auth";

const BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:7071/api";

export type Frequency = "Annual" | "SemiAnnual" | "Quarterly";

export interface Tool {
  id: number;
  name: string;
  toolOwnerUpn: string;
  toolOwnerDisplayName: string;
  frequency: Frequency;
  controlId: string;
  stage1Days: number;
  stage2Days: number;
  reminderStartS1: number;
  reminderStartS2: number;
  docLibraryPath: string | null;
  /** Optional UPN to use as Stage 2 reviewer instead of the manager-chain
   *  fallback. Resolved at Stage 2 start by determineStage2Reviewer; if the
   *  override UPN no longer exists or has accountEnabled=false at that point,
   *  the activity logs an admin alert and falls back to the manager chain. */
  overrideS2ReviewerUpn: string | null;
  /** true = active (cycles are scheduled); false = deactivated (skipped by
   *  cycle scheduling — no NEW cycles — while any in-flight cycle still
   *  completes normally). Surfaced in the UI as the "Deactivate Tool"
   *  checkbox, where checked = isActive false. */
  isActive: boolean;
}

export interface CreateToolPayload {
  name: string;
  toolOwnerUpn: string;
  toolOwnerDisplayName: string;
  frequency: Frequency;
  controlId: string;
  stage1Days?: number;
  stage2Days?: number;
  reminderStartS1?: number;
  reminderStartS2?: number;
  docLibraryPath?: string;
  /** Optional UPN — omit, null, or empty string all mean "no override". */
  overrideS2ReviewerUpn?: string | null;
  /** Omit to default to active. false = create the tool already deactivated. */
  isActive?: boolean;
}

/** All fields optional; the server applies a partial update. `name` is NOT updatable. */
export interface UpdateToolPayload {
  toolOwnerUpn?: string;
  toolOwnerDisplayName?: string;
  frequency?: Frequency;
  controlId?: string;
  stage1Days?: number;
  stage2Days?: number;
  reminderStartS1?: number;
  reminderStartS2?: number;
  docLibraryPath?: string;
  /** Three-state semantic on PATCH:
   *   - key absent       → no change
   *   - key with `null`  → clear the override
   *   - key with `""`    → also clears (server normalizes empty string to null)
   *   - non-empty string → set after email-shape validation */
  overrideS2ReviewerUpn?: string | null;
  /** Omit → no change. true = active, false = deactivated. */
  isActive?: boolean;
}

export async function listTools(): Promise<Tool[]> {
  const resp = await axios.get(`${BASE_URL}/manage/tools`, { headers: getAuthHeaders() });
  return resp.data.tools as Tool[];
}

/** How many Entra groups each tool currently has in its roster. */
export interface ToolGroupCount {
  toolName: string;
  groupCount: number;
}

/**
 * Read-only companion to listTools, used for the registry's "Groups" column.
 * Fetched independently of the tools list so a failure here degrades to "—"
 * in that one column rather than breaking the Tools Registry page.
 */
export async function listToolGroupCounts(): Promise<ToolGroupCount[]> {
  const resp = await axios.get(`${BASE_URL}/manage/tools/group-counts`, { headers: getAuthHeaders() });
  return resp.data.counts as ToolGroupCount[];
}

export async function createTool(payload: CreateToolPayload): Promise<Tool> {
  const resp = await axios.post(
    `${BASE_URL}/manage/tools`,
    payload,
    { headers: getAuthHeaders() }
  );
  return resp.data as Tool;
}

export async function updateTool(name: string, payload: UpdateToolPayload): Promise<Tool> {
  const resp = await axios.patch(
    `${BASE_URL}/manage/tools/${encodeURIComponent(name)}`,
    payload,
    { headers: getAuthHeaders() }
  );
  return resp.data as Tool;
}

export async function deleteTool(name: string): Promise<void> {
  await axios.delete(
    `${BASE_URL}/manage/tools/${encodeURIComponent(name)}`,
    { headers: getAuthHeaders() }
  );
}
